import { Entity, IEntity } from '@lycoris-nubila/ngx-lycoris';
import { Observable, of } from 'rxjs';

import { IModule, Module } from 'module/module';
import { ModuleQuestionType } from 'module/page/question/model/module-question.type';

import {
	IModuleQuestionField,
	ModuleQuestionField,
} from './field/module-question-field';
import {
	IModuleQuestionI18n,
	ModuleQuestionI18n,
} from './module-question-i18n';

export interface IModuleQuestion extends IEntity {
	readonly name: string;
	readonly position: number;
	readonly condition: string;
	readonly uniqFilter: string;
	readonly canDuplicate: boolean;
	readonly canAddSibling: boolean;
	readonly sourceModuleId: string;
	readonly sourceQuestionId: string;
	readonly type: ModuleQuestionType;
	readonly fields: ReadonlyArray<IModuleQuestionField>;
	readonly cssClass: string;
	readonly gridClass: string;
	readonly localizations: ReadonlyArray<IModuleQuestionI18n>;
	readonly localization$: Observable<IModuleQuestionI18n>;
	readonly sourceQuestion$: Observable<IModuleQuestion>;
	readonly sourceModule$: Observable<IModule>;
}

export class ModuleQuestion extends Entity implements IModuleQuestion {
	private _name: string;
	private _position: number;
	private _condition: string;
	private _uniqFilter: string;
	private _canDuplicate: boolean;
	private _canAddSibling: boolean;
	private _sourceModuleId: string;
	private _sourceQuestionId: string;
	private _type: ModuleQuestionType;
	private _fields: ModuleQuestionField[];
	private _localizations: ModuleQuestionI18n[];
	private _cssClass: string;
	private _gridClass: string;
	private _localization$: Observable<ModuleQuestionI18n>;
	private _sourceQuestion$: Observable<ModuleQuestion>;
	private _sourceModule$: Observable<Module>;

	constructor(
		id: string,
		name: string,
		position: number,
		condition: string,
		uniqFilter: string,
		canDuplicate: boolean,
		canAddSibling: boolean,
		sourceModuleId: string,
		updateTimestamp: number,
		sourceQuestionId: string,
		type: ModuleQuestionType,
		fields: ModuleQuestionField[],
		localizations: ModuleQuestionI18n[],
		cssClass: string,
		gridClass: string,
		localization$: Observable<ModuleQuestionI18n> = of(null),
		sourceQuestion$: Observable<ModuleQuestion> = of(null),
		sourceModule$: Observable<Module> = of(null)
	) {
		super(id, null, updateTimestamp);
		this._name = name;
		this._type = type;
		this._position = position;
		this._condition = condition;
		this._uniqFilter = uniqFilter;
		this._canDuplicate = canDuplicate;
		this._canAddSibling = canAddSibling;
		this._sourceModuleId = sourceModuleId;
		this._sourceQuestionId = sourceQuestionId;
		this._fields = fields;
		this._localizations = localizations;
		this._cssClass = cssClass;
		this._gridClass = gridClass;
		this._localization$ = localization$;
		this._sourceQuestion$ = sourceQuestion$;
		this._sourceModule$ = sourceModule$;
	}

	public get name(): string {
		return this._name;
	}

	public get position(): number {
		return this._position;
	}

	public get condition(): string {
		return this._condition;
	}

	public get uniqFilter(): string {
		return this._uniqFilter;
	}

	public get canDuplicate(): boolean {
		return this._canDuplicate;
	}

	public get canAddSibling(): boolean {
		return this._canAddSibling;
	}

	public get sourceModuleId(): string {
		return this._sourceModuleId;
	}

	public get sourceQuestionId(): string {
		return this._sourceQuestionId;
	}

	public get type(): ModuleQuestionType {
		return this._type;
	}

	public get fields(): ModuleQuestionField[] {
		return this._fields;
	}

	public get localizations(): ModuleQuestionI18n[] {
		return this._localizations;
	}

	public get cssClass(): string {
		return this._cssClass;
	}

	public get gridClass(): string {
		return this._gridClass;
	}

	public get localization$(): Observable<ModuleQuestionI18n> {
		return this._localization$;
	}

	public get sourceQuestion$(): Observable<ModuleQuestion> {
		return this._sourceQuestion$;
	}

	public get sourceModule$(): Observable<Module> {
		return this._sourceModule$;
	}
}
